import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getServerMastersList, previewBranchCopy, copyBranchToServer } from '../utilities/api';
import '../custom.css';

const NB_COLOR = '#6366f1';

const TABLES = [
    { value: 'application_setting', label: 'Application Settings', icon: 'pi-cog' },
    { value: 'company_creation', label: 'Company', icon: 'pi-building' },
    { value: 'locations', label: 'Locations', icon: 'pi-map-marker' },
    { value: 'masters_creation', label: 'Masters Creation', icon: 'pi-database' },
    { value: 'product_category', label: 'Product Category', icon: 'pi-tags' },
    { value: 'product', label: 'Product', icon: 'pi-box' },
    { value: 'users', label: 'Users', icon: 'pi-users' },
    { value: 'printer_details', label: 'Printer Details', icon: 'pi-print' },
    { value: 'supplier', label: 'Supplier', icon: 'pi-truck' },
    { value: 'tables', label: 'Tables', icon: 'pi-th-large' },
    { value: 'sms_authorize_final', label: 'SMS Authorization', icon: 'pi-envelope' }
];
const TABLE_VALUES = TABLES.map((t) => t.value);
const TABLE_LABEL = Object.fromEntries(TABLES.map((t) => [t.value, t.label]));
const TABLE_ICON = Object.fromEntries(TABLES.map((t) => [t.value, t.icon]));

// Copied together, one HTTP call per group, so id remapping (locations ->
// company_creation.location_id, product_category -> product.prcat_cat_id)
// stays correct within one request — see Server\BranchCopy::copyToServer().
const COPY_GROUPS = [['locations', 'company_creation'], ['masters_creation'], ['application_setting'], ['product_category', 'product'], ['users'], ['printer_details'], ['supplier'], ['tables'], ['sms_authorize_final']];

const COPY_STATUS_SEVERITY = { copied: 'success', conflict: 'danger', unsupported: 'danger', empty: 'secondary', error: 'danger', pending: 'secondary', copying: 'info' };
const COPY_STATUS_ICON = { copied: 'pi-check', conflict: 'pi-exclamation-triangle', unsupported: 'pi-ban', empty: 'pi-minus', error: 'pi-times', pending: 'pi-clock', copying: 'pi-spin pi-spinner' };
const COPY_STATUS_LABEL = { copied: 'Copied', conflict: 'Skipped — exists', unsupported: 'Unsupported', empty: 'Nothing to copy', error: 'Failed', pending: 'Pending', copying: 'Copying…' };
const COPY_ROW_CLASS = { copied: 'nb-row-ok', conflict: 'nb-row-muted', unsupported: 'nb-row-bad', empty: 'nb-row-muted', error: 'nb-row-bad' };

const errorsColumn = {
    header: 'Details',
    body: (row) => ((row.errors || []).length > 0 ? <span style={{ color: 'var(--red-500)', fontSize: '0.85rem' }}>{row.errors.join('; ')}</span> : '')
};

const initials = (label) =>
    (label || '?')
        .replace(/\(.*\)/, '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase();

const StatTile = ({ label, value, color, icon }) => (
    <div className="nb-stat flex align-items-center gap-3 border-round shadow-1 p-3 surface-card" style={{ borderLeft: `4px solid ${color}`, minWidth: 150 }}>
        <div className="flex align-items-center justify-content-center border-round-lg" style={{ width: 40, height: 40, background: `${color}1a` }}>
            <i className={`pi ${icon}`} style={{ fontSize: '1.2rem', color }} />
        </div>
        <div>
            <div className="text-2xl font-bold line-height-1">{value}</div>
            <div className="text-color-secondary text-sm">{label}</div>
        </div>
    </div>
);

const Newbranch = () => {
    const [clients, setClients] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [loadingLookups, setLoadingLookups] = useState(false);

    const [sourceClient, setSourceClient] = useState(null);
    const [sourceBranch, setSourceBranch] = useState(null);
    const [destClient, setDestClient] = useState(null);
    const [destBranch, setDestBranch] = useState(null);

    const [selectedTables, setSelectedTables] = useState(TABLE_VALUES);

    const [previewRows, setPreviewRows] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewKey, setPreviewKey] = useState(null);

    const [copyRows, setCopyRows] = useState(null);
    const [copyLoading, setCopyLoading] = useState(false);
    const [copyProgress, setCopyProgress] = useState(null); // { current, total, label }

    const [collapsed, setCollapsed] = useState({});
    const toggleCollapsed = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const toast = useRef(null);

    useEffect(() => {
        const loadLookups = async () => {
            setLoadingLookups(true);
            try {
                const [clientRes, branchRes] = await Promise.allSettled([getServerMastersList('client_creation'), getServerMastersList('branch_creation')]);
                const toArray = (res) => (res.status === 'fulfilled' && Array.isArray(res.value) ? res.value : res.status === 'fulfilled' && Array.isArray(res.value?.data) ? res.value.data : []);
                const clientRows = toArray(clientRes);
                const branchRows = toArray(branchRes);
                setClients(clientRows.map((c) => ({ label: `${c.client_name} (${c.client_id})`, value: String(c.client_id) })));
                setAllBranches(branchRows.map((b) => ({ label: `${b.branch_name} (${b.branch_id})`, value: String(b.branch_id), clientId: String(b.client_id) })));
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load clients/branches' });
            } finally {
                setLoadingLookups(false);
            }
        };
        loadLookups();
    }, []);

    const branchOptionsFor = (clientId) => allBranches.filter((b) => b.clientId === clientId);
    const sourceClientLabel = clients.find((c) => c.value === sourceClient)?.label;
    const destClientLabel = clients.find((c) => c.value === destClient)?.label;

    const resetResults = () => {
        setPreviewRows(null);
        setCopyRows(null);
        setCopyProgress(null);
    };

    const onSourceClientChange = (value) => {
        setSourceClient(value);
        setSourceBranch(null);
        resetResults();
    };
    const onDestClientChange = (value) => {
        setDestClient(value);
        setDestBranch(null);
        resetResults();
    };
    const onSourceBranchChange = (value) => {
        setSourceBranch(value);
        resetResults();
    };
    const onDestBranchChange = (value) => {
        setDestBranch(value);
        resetResults();
    };
    const toggleTable = (value) => {
        setSelectedTables((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]));
        resetResults();
    };
    const toggleAllTables = () => {
        const allOn = TABLE_VALUES.every((t) => selectedTables.includes(t));
        setSelectedTables(allOn ? [] : TABLE_VALUES);
        resetResults();
    };

    const isSameBranch = sourceClient && sourceBranch && destClient && destBranch && sourceClient === destClient && sourceBranch === destBranch;
    const readyToCheck = sourceClient && sourceBranch && destClient && destBranch && !isSameBranch && selectedTables.length > 0;

    const currentKey = () => JSON.stringify({ sourceClient, sourceBranch, destClient, destBranch, selectedTables: [...selectedTables].sort() });

    // As soon as source + destination + tables are all picked, check
    // automatically whether the destination already has data — the alert
    // shows up without an extra click.
    useEffect(() => {
        if (!readyToCheck) return undefined;
        let cancelled = false;
        const run = async () => {
            setPreviewLoading(true);
            try {
                const payload = {
                    source_client_id: Number(sourceClient),
                    source_branch_id: Number(sourceBranch),
                    dest_client_id: Number(destClient),
                    dest_branch_id: Number(destBranch),
                    tables: selectedTables
                };
                const result = await previewBranchCopy(payload);
                if (cancelled) return;
                if (result?.status === 'success') {
                    setPreviewRows(result.tables || []);
                    setPreviewKey(currentKey());
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Check failed', detail: result?.message || 'Unknown error' });
                }
            } catch (error) {
                if (!cancelled) toast.current?.show({ severity: 'error', summary: 'Check failed', detail: error?.response?.data?.message || error.message });
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceClient, sourceBranch, destClient, destBranch, selectedTables.join(',')]);

    const conflicts = (previewRows || []).filter((r) => r.will_conflict || r.unsupported);
    const copyable = (previewRows || []).filter((r) => !r.will_conflict && !r.unsupported && r.source_count > 0);
    const previewStale = previewRows !== null && previewKey !== currentKey();
    const canCopy = previewRows !== null && !previewStale && !previewLoading && copyable.length > 0 && !copyLoading;

    const totalSourceRows = previewRows ? previewRows.reduce((s, r) => s + (r.source_count || 0), 0) : null;
    const totalDestRows = previewRows ? previewRows.reduce((s, r) => s + (r.dest_existing || 0), 0) : null;

    const previewByTable = Object.fromEntries((previewRows || []).map((r) => [r.table, r]));
    const copyByTable = Object.fromEntries((copyRows || []).map((r) => [r.table, r]));

    // Combines preview + copy results into one badge per table tile, so it's
    // obvious at a glance which tables are ready/blocked before copying and
    // which are pending/copying/done once the copy is running or finished.
    const tileStatus = (value) => {
        const copy = copyByTable[value];
        if (copy) {
            if (copy.status === 'copying') return { color: '#3b82f6', icon: 'pi-spin pi-spinner', title: 'Copying…' };
            if (copy.status === 'pending') return { color: '#94a3b8', icon: 'pi-clock', title: 'Queued to copy' };
            if (copy.status === 'copied') return { color: '#22c55e', icon: 'pi-check', title: `Copied — ${copy.inserted} row(s)` };
            if (copy.status === 'conflict') return { color: '#f59e0b', icon: 'pi-exclamation-triangle', title: copy.message };
            if (copy.status === 'unsupported') return { color: '#ef4444', icon: 'pi-ban', title: copy.message };
            if (copy.status === 'empty') return { color: '#94a3b8', icon: 'pi-minus', title: copy.message };
            return { color: '#ef4444', icon: 'pi-times', title: copy.message || 'Failed' };
        }
        const preview = previewByTable[value];
        if (preview) {
            if (preview.unsupported) return { color: '#ef4444', icon: 'pi-ban', title: preview.unsupported_reason };
            if (preview.will_conflict) return { color: '#f59e0b', icon: 'pi-exclamation-triangle', title: `Destination already has ${preview.dest_existing} row(s)` };
            if (preview.source_count === 0) return { color: '#94a3b8', icon: 'pi-minus', title: 'Nothing to copy' };
            return { color: '#22c55e', icon: 'pi-check-circle', title: `${preview.source_count} row(s) ready to copy` };
        }
        return null;
    };

    const confirmCopy = () => {
        confirmDialog({
            header: 'Copy to server database?',
            message: (
                <div>
                    <p>
                        This copies data from client {sourceClient} / branch {sourceBranch} into client {destClient} / branch {destBranch} on the server (master) database.
                    </p>
                    <p>Will copy: {copyable.map((r) => `${TABLE_LABEL[r.table]} (${r.source_count})`).join(', ')}</p>
                    {conflicts.length > 0 && <p style={{ color: 'var(--red-500)' }}>Will be skipped: {conflicts.map((r) => TABLE_LABEL[r.table]).join(', ')}</p>}
                </div>
            ),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Copy to Server',
            rejectLabel: 'Cancel',
            accept: runCopyToServer
        });
    };

    const runCopyToServer = async () => {
        const groups = COPY_GROUPS.map((g) => g.filter((t) => selectedTables.includes(t))).filter((g) => g.length > 0);
        if (groups.length === 0) return;

        setCopyLoading(true);
        // Seed every selected table as "pending" up front so the results list
        // shows the full picture immediately, instead of tables only
        // appearing once their batch happens to finish.
        let collected = groups.flat().map((t) => ({ table: t, status: 'pending', inserted: 0, message: 'Waiting to be copied', errors: [] }));
        setCopyRows([...collected]);

        try {
            for (let i = 0; i < groups.length; i++) {
                const group = groups[i];
                setCopyProgress({ current: i, total: groups.length, label: `Copying ${group.map((t) => TABLE_LABEL[t]).join(', ')}…` });

                collected = collected.map((r) => (group.includes(r.table) ? { ...r, status: 'copying', message: 'Copying…' } : r));
                setCopyRows([...collected]);

                const payload = {
                    source_client_id: Number(sourceClient),
                    source_branch_id: Number(sourceBranch),
                    dest_client_id: Number(destClient),
                    dest_branch_id: Number(destBranch),
                    tables: group
                };
                // eslint-disable-next-line no-await-in-loop
                const result = await copyBranchToServer(payload);
                const rows =
                    result?.status === 'success'
                        ? result.results || []
                        : group.map((t) => ({ table: t, status: 'error', inserted: 0, message: result?.message || 'Request failed', errors: [] }));

                collected = collected.map((r) => {
                    const updated = rows.find((x) => x.table === r.table);
                    return updated ? { ...r, ...updated } : r;
                });
                setCopyRows([...collected]);
                setCopyProgress({ current: i + 1, total: groups.length, label: i + 1 < groups.length ? `Copying ${groups[i + 1].map((t) => TABLE_LABEL[t]).join(', ')}…` : 'Done' });
            }

            const failed = collected.some((r) => r.status === 'error');
            toast.current?.show({
                severity: failed ? 'warn' : 'success',
                summary: 'Copy to server finished',
                detail: failed ? 'Completed with some tables failed — see results below' : 'Data copied to the server database.'
            });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Copy failed', detail: error?.response?.data?.message || error.message });
        } finally {
            setCopyLoading(false);
        }
    };

    const copySummary = copyRows &&
        !copyLoading && {
            copied: copyRows.filter((r) => r.status === 'copied').length,
            skipped: copyRows.filter((r) => r.status !== 'copied').length,
            rows: copyRows.reduce((s, r) => s + (r.inserted || 0), 0)
        };

    return (
        <div className="nb-page">
            <style>{`
                .nb-tile { position: relative; display:flex; flex-direction:column; align-items:flex-start; gap:.5rem; padding:.75rem .9rem; border-radius:12px; border:1px solid var(--surface-border); background:var(--surface-card); cursor:pointer; transition: all .15s ease; min-width:132px; }
                .nb-tile:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,.08); }
                .nb-tile.active { border-color: var(--tile-color); background: color-mix(in srgb, var(--tile-color) 10%, var(--surface-card)); }
                .nb-tile .nb-check { position:absolute; top:6px; right:6px; color: var(--tile-color); }
                .nb-tile .nb-icon { font-size:1.1rem; color: var(--tile-color); }
                .nb-tile .nb-status-dot { position:absolute; bottom:6px; right:6px; width:16px; height:16px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; box-shadow: 0 0 0 2px var(--surface-card); }
                .nb-tile .nb-status-dot i { font-size: 9px; }
                .nb-row-ok { background: color-mix(in srgb, #22c55e 6%, transparent); }
                .nb-row-bad { background: color-mix(in srgb, #ef4444 7%, transparent); }
                .nb-row-muted { opacity: .65; }
                .nb-pulse { animation: nbPulse 1.4s ease-in-out infinite; }
                @keyframes nbPulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
                .nb-card .p-card-body { padding: 1rem 1.25rem; }
                .nb-card .p-card-content { padding: 0; }
                .nb-header { padding-bottom: .9rem; border-bottom: 1px solid var(--surface-border); margin-bottom: 1rem; }
                .nb-overview { display:flex; flex-wrap:wrap; align-items:stretch; gap: .75rem; margin-bottom: 1rem; }
                .nb-client-box { flex: 1 1 260px; max-width: 320px; }
                .nb-stats-row { flex: 2 1 420px; display:flex; flex-wrap:wrap; gap:.75rem; }
                .nb-stats-row .nb-stat { flex: 1 1 140px; }
                .nb-section { border-radius: 12px; border: 1px solid var(--surface-border); border-left-width: 4px; border-left-style: solid; background: var(--surface-card); overflow: hidden; transition: box-shadow .15s ease, border-color .15s ease; }
                .nb-section:hover { box-shadow: 0 2px 12px rgba(0,0,0,.06); }
                .nb-section-header { padding: .6rem 1rem; border-bottom: 1px solid var(--surface-border); background: var(--surface-50); }
                .nb-section-body { padding: 1rem; animation: nbFadeIn .15s ease; }
                .nb-sections { display:flex; flex-direction:column; gap: .75rem; }
                @keyframes nbFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Card className="nb-card">
                <div className="nb-header flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center justify-content-center border-circle flex-shrink-0" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                            <i className="pi pi-sitemap text-white text-xl" />
                        </div>
                        <div>
                            <div className="text-xl font-bold line-height-2">New Branch — Copy Branch Data</div>
                            <div className="text-color-secondary text-sm">Clone a branch&apos;s masters into a new or existing branch, on the server (master) database.</div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Badge value={`${selectedTables.length}/${TABLE_VALUES.length} tables`} severity="info" style={{ fontSize: '0.8rem' }} />
                    </div>
                </div>

                <div className="nb-overview">
                    <div className="nb-client-box border-round shadow-1 p-3 surface-card" style={{ borderTop: '4px solid #6366f1' }}>
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-sign-out" style={{ color: '#6366f1' }} />
                            <span className="font-bold">Source</span>
                        </div>
                        <Dropdown value={sourceClient} options={clients} onChange={(e) => onSourceClientChange(e.value)} placeholder="Select client" filter showClear className="w-full mb-3" loading={loadingLookups} />
                        <Dropdown value={sourceBranch} options={branchOptionsFor(sourceClient)} onChange={(e) => onSourceBranchChange(e.value)} placeholder="Select branch" filter showClear disabled={!sourceClient} className="w-full" />
                        {sourceClient && (
                            <div className="flex align-items-center gap-2 mt-3">
                                <Avatar label={initials(sourceClientLabel)} shape="circle" style={{ backgroundColor: '#6366f1', color: '#fff' }} />
                                <span className="text-sm">{sourceClientLabel}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex align-items-center justify-content-center" style={{ flex: '0 0 auto' }}>
                        <i className="pi pi-arrow-right text-2xl text-color-secondary rotate-90 md:rotate-0" />
                    </div>

                    <div className="nb-client-box border-round shadow-1 p-3 surface-card" style={{ borderTop: '4px solid #22c55e' }}>
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-sign-in text-green-500" />
                            <span className="font-bold">Destination</span>
                        </div>
                        <Dropdown value={destClient} options={clients} onChange={(e) => onDestClientChange(e.value)} placeholder="Select client" filter showClear className="w-full mb-3" loading={loadingLookups} />
                        <Dropdown value={destBranch} options={branchOptionsFor(destClient)} onChange={(e) => onDestBranchChange(e.value)} placeholder="Select branch" filter showClear disabled={!destClient} className="w-full" />
                        {destClient && (
                            <div className="flex align-items-center gap-2 mt-3">
                                <Avatar label={initials(destClientLabel)} shape="circle" style={{ backgroundColor: '#22c55e', color: '#fff' }} />
                                <span className="text-sm">{destClientLabel}</span>
                            </div>
                        )}
                    </div>

                    <div className="nb-stats-row">
                        <StatTile label="Source Rows" value={totalSourceRows ?? '—'} color="#6366f1" icon="pi-database" />
                        <StatTile label="Destination Existing" value={totalDestRows ?? '—'} color="#f59e0b" icon="pi-server" />
                        <StatTile label="Selected Tables" value={selectedTables.length} color="#22c55e" icon="pi-check-square" />
                    </div>
                </div>

                {isSameBranch && <Message severity="error" className="mb-3 w-full" text="Source and destination are the same branch — pick a different destination." />}

                {(!sourceClient || !sourceBranch || !destClient || !destBranch) && (
                    <div className="flex flex-column align-items-center justify-content-center text-center border-round surface-50 mb-3" style={{ padding: '2rem 1rem' }}>
                        <i className="pi pi-arrow-up text-3xl text-color-secondary mb-2" />
                        <div className="text-lg font-medium">Select a source and destination branch above to get started</div>
                        <div className="text-color-secondary text-sm mt-1">Once both are picked, this checks the destination and shows exactly what&apos;s ready to copy.</div>
                    </div>
                )}

                <div className="nb-sections">
                    <div className="nb-section" style={{ borderLeftColor: NB_COLOR }}>
                        <div className="nb-section-header flex flex-wrap align-items-center justify-content-between gap-2">
                            <span className="flex align-items-center gap-2">
                                <Button
                                    icon={`pi ${collapsed.copy ? 'pi-chevron-right' : 'pi-chevron-down'}`}
                                    text
                                    rounded
                                    size="small"
                                    aria-label="Toggle section"
                                    onClick={() => toggleCollapsed('copy')}
                                />
                                <i className="pi pi-database" style={{ color: NB_COLOR }} />
                                <span className="font-bold" style={{ color: NB_COLOR }}>
                                    Branch → Server (Copy)
                                </span>
                                <Badge value={`${selectedTables.length}/${TABLE_VALUES.length}`} style={{ backgroundColor: NB_COLOR }} />
                            </span>
                            <Button label="Copy to Server" icon="pi pi-database" size="small" loading={copyLoading} disabled={!canCopy} onClick={confirmCopy} />
                        </div>
                        {!collapsed.copy && (
                            <div className="nb-section-body">
                                <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <p className="text-color-secondary text-sm m-0">
                                        Writes into the server (master) database only — a table already holding rows for the destination branch is skipped, never overwritten.
                                    </p>
                                    <Button label={TABLE_VALUES.every((t) => selectedTables.includes(t)) ? 'Clear all' : 'Select all'} link size="small" onClick={toggleAllTables} />
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {TABLES.map((t) => {
                                        const active = selectedTables.includes(t.value);
                                        const status = tileStatus(t.value);
                                        return (
                                            <div key={t.value} className={`nb-tile ${active ? 'active' : ''}`} style={{ '--tile-color': NB_COLOR }} onClick={() => toggleTable(t.value)} role="button" tabIndex={0}>
                                                {active && <i className="pi pi-check-circle nb-check" />}
                                                <i className={`pi ${t.icon} nb-icon`} />
                                                <span className="text-sm font-medium">{t.label}</span>
                                                {status && <span className="nb-status-dot" style={{ background: status.color }} title={status.title}><i className={`pi ${status.icon}`} /></span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {copyProgress && (
                    <div className="nb-section mt-3" style={{ borderLeftColor: NB_COLOR }}>
                        <div className="nb-section-body">
                            <div className="flex justify-content-between align-items-center text-sm mb-2">
                                <span className="flex align-items-center gap-2">
                                    <i className={`pi pi-spin pi-spinner ${copyLoading ? 'nb-pulse' : ''}`} style={{ color: NB_COLOR, display: copyLoading ? 'inline' : 'none' }} />
                                    <span className="font-medium">{copyProgress.label}</span>
                                </span>
                                <span className="text-color-secondary">
                                    {copyProgress.current}/{copyProgress.total}
                                </span>
                            </div>
                            <ProgressBar value={Math.round((copyProgress.current / copyProgress.total) * 100)} showValue={false} color={NB_COLOR} style={{ height: 10 }} />
                        </div>
                    </div>
                )}

                {previewLoading && <Message severity="info" className="mt-3 w-full" text="Checking destination branch for existing data..." />}
                {!previewLoading && previewRows && conflicts.length > 0 && (
                    <Message
                        severity="warn"
                        className="mt-3 w-full"
                        text={`Already has data (will be skipped): ${conflicts.map((r) => `${TABLE_LABEL[r.table]} (${r.dest_existing} row(s)${r.unsupported ? ', unsupported' : ''})`).join(', ')}`}
                    />
                )}
                {!previewLoading && previewRows && conflicts.length === 0 && copyable.length > 0 && (
                    <Message severity="success" className="mt-3 w-full" text="No existing data in the destination branch for the selected tables — safe to copy." />
                )}
                {!previewLoading && previewRows && copyable.length === 0 && conflicts.length === previewRows.length && (
                    <Message severity="error" className="mt-3 w-full" text="Every selected table already has data (or is unsupported) at the destination — nothing to copy." />
                )}
                {previewStale && <Message severity="warn" className="mt-3 w-full" text="Selection changed — re-checking destination automatically." />}

                {previewRows && !previewLoading && (
                    <div className="nb-section mt-3" style={{ borderLeftColor: NB_COLOR }}>
                        <div className="nb-section-header">
                            <span className="font-bold" style={{ color: NB_COLOR }}>
                                Destination Check
                            </span>
                        </div>
                        <div className="nb-section-body">
                            <DataTable value={previewRows} size="small" stripedRows className="border-round overflow-hidden">
                                <Column
                                    header="Table"
                                    body={(row) => (
                                        <span className="flex align-items-center gap-2">
                                            <i className={`pi ${TABLE_ICON[row.table]}`} />
                                            {TABLE_LABEL[row.table]}
                                        </span>
                                    )}
                                />
                                <Column field="source_count" header="Source Rows" />
                                <Column field="dest_existing" header="Destination Existing" />
                                <Column
                                    header="Status"
                                    body={(row) =>
                                        row.unsupported ? (
                                            <Tag severity="danger" value="Unsupported" title={row.unsupported_reason} />
                                        ) : row.will_conflict ? (
                                            <Tag severity="danger" value="Already exists — will skip" />
                                        ) : row.source_count === 0 ? (
                                            <Tag severity="secondary" value="Nothing to copy" />
                                        ) : (
                                            <Tag severity="success" value="Will copy" />
                                        )
                                    }
                                />
                            </DataTable>
                        </div>
                    </div>
                )}

                {copyRows && copyRows.length > 0 && (
                    <div className="nb-section mt-3" style={{ borderLeftColor: NB_COLOR }}>
                        <div className="nb-section-header">
                            <span className="font-bold" style={{ color: NB_COLOR }}>
                                Copy Result
                            </span>
                        </div>
                        <div className="nb-section-body">
                            {copySummary && (
                                <div className="flex flex-wrap gap-3 mb-3">
                                    <StatTile label="Tables copied" value={copySummary.copied} color="#22c55e" icon="pi-check-circle" />
                                    <StatTile label="Tables skipped" value={copySummary.skipped} color="#f59e0b" icon="pi-exclamation-circle" />
                                    <StatTile label="Rows inserted" value={copySummary.rows} color="#6366f1" icon="pi-database" />
                                </div>
                            )}
                            <DataTable value={copyRows} size="small" stripedRows className="border-round overflow-hidden" rowClassName={(row) => COPY_ROW_CLASS[row.status] || ''}>
                                <Column field="table" header="Table" body={(row) => TABLE_LABEL[row.table] || row.table} />
                                <Column
                                    header="Status"
                                    body={(row) => (
                                        <Tag severity={COPY_STATUS_SEVERITY[row.status] || 'info'} icon={`pi ${COPY_STATUS_ICON[row.status] || 'pi-info-circle'}`} value={COPY_STATUS_LABEL[row.status] || row.status} />
                                    )}
                                />
                                <Column field="inserted" header="Inserted" />
                                <Column field="message" header="Message" />
                                <Column {...errorsColumn} />
                            </DataTable>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Newbranch;
